import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  FileText,
  Eye,
  Receipt,
  Wallet,
  DollarSign,
  Users,
  BarChart3,
  Database,
  LogOut,
  Menu,
  X,
  FileCheck,
  Settings,
  CreditCard,
  Calendar,
  Globe,
  Cake,
  Sparkles,
  UtensilsCrossed,
  Package
} from 'lucide-react';
import { useState } from 'react';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const menuItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'regional_manager'] },
    { path: '/website-bookings', label: 'Update Bills', icon: Globe, roles: ['admin', 'manager', 'regional_manager', 'auditor'] },
    { path: '/add-bill', label: 'Add Bill', icon: FileText, roles: ['admin'] },
    { path: '/view-bills', label: 'View Bills', icon: Eye, roles: ['admin', 'manager', 'regional_manager', 'auditor'] },
    { path: '/gst-bills', label: 'GST Bills', icon: FileCheck, roles: ['admin', 'auditor'] },
    { path: '/online-payments', label: 'Online Payments', icon: CreditCard, roles: ['admin', 'auditor'] },
    { path: '/branch-expenses', label: 'Branch Expenses', icon: Receipt, roles: ['admin', 'manager', 'regional_manager'] },
    { path: '/owner-expenses', label: 'Owner Expenses', icon: DollarSign, roles: ['admin'] },
    { path: '/cash-handover', label: 'Cash Handover', icon: Wallet, roles: ['admin', 'manager', 'regional_manager'] },
    { path: '/cash-bookings', label: 'Branch Bookings', icon: Calendar, roles: ['admin', 'manager', 'regional_manager'] },
    { path: '/users', label: 'User Management', icon: Users, roles: ['admin'] },
    { path: '/master-data', label: 'Master Data', icon: Database, roles: ['admin'] },
    { path: '/gst-config', label: 'GST Configuration', icon: Settings, roles: ['admin'] },
    { path: '/reports', label: 'Reports', icon: BarChart3, roles: ['admin'] },
    { path: '/report/cakes', label: 'Cake Report', icon: Cake, roles: ['admin', 'auditor'] },
    { path: '/report/decorations', label: 'Decorations Report', icon: Sparkles, roles: ['admin', 'auditor'] },
    { path: '/report/food-beverages', label: 'F&B Report', icon: UtensilsCrossed, roles: ['admin', 'auditor'] },
    { path: '/report/miscellaneous', label: 'Misc Items Report', icon: Package, roles: ['admin', 'auditor'] },
  ];

  const filteredMenuItems = menuItems.filter(item => item.roles.includes(user?.role));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <button
                data-testid="menu-toggle-btn"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
              >
                {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <h1 className="text-2xl font-bold ml-2 lg:ml-0" style={{ fontFamily: 'Space Grotesk' }}>
                <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Billing Application
                </span>
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-900" data-testid="user-name">{user?.username}</p>
                <p className="text-xs text-slate-500" data-testid="user-role">{user?.role === 'regional_manager' ? 'Regional Manager' : user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}</p>
                {user?.branch_name && (
                  <p className="text-xs text-slate-500" data-testid="user-branch">{user.branch_name}</p>
                )}
              </div>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <LogOut size={16} />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200
            transform transition-transform duration-200 ease-in-out lg:translate-x-0
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            mt-16 lg:mt-0
          `}
        >
          <nav className="h-full overflow-y-auto py-6 px-3">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 mb-1 rounded-lg
                    transition-all duration-200
                    ${isActive(item.path)
                      ? 'bg-indigo-50 text-indigo-700 font-medium shadow-sm'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }
                  `}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
